import { useEffect, useState, useCallback } from "react";
import { API } from "@/App";
import { format, subDays, addDays } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Flame, Snowflake, Users, Bell, Plus } from "lucide-react";
import { ExtraClassDialog } from "@/components/ExtraClassDialog";
import { DateInputTR } from "@/components/ui/date-picker-tr";

const WD = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const WD_LONG = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];
const ymd = (d) => format(d, "yyyy-MM-dd");
const PRESETS = [
  { k: "4w", l: "Son 4 hafta", range: () => [subDays(new Date(), 28), new Date()] },
  { k: "3m", l: "Son 3 ay", range: () => [subDays(new Date(), 90), new Date()] },
  { k: "next", l: "Önümüzdeki 7 gün", range: () => [new Date(), addDays(new Date(), 7)] },
];
const heat = (p) => (p >= 90 ? "bg-emerald-700 text-white" : p >= 70 ? "bg-emerald-500 text-white" : p >= 50 ? "bg-emerald-300 text-emerald-950" : p >= 25 ? "bg-emerald-100 text-emerald-900" : "bg-slate-100 text-slate-500");

const Stat = ({ label, value, sub, testId }) => (
  <Card className="border-0 shadow-sm"><CardContent className="p-4">
    <p className="text-xs text-grey">{label}</p>
    <p className="text-2xl font-bold text-navy mt-1" data-testid={testId}>{value}</p>
    {sub && <p className="text-[11px] text-grey mt-0.5">{sub}</p>}
  </CardContent></Card>
);

const SlotList = ({ title, icon: Icon, tone, items, testId, onExtra }) => (
  <Card className="border-0 shadow-sm"><CardContent className="p-4" data-testid={testId}>
    <p className={`font-semibold text-sm flex items-center gap-1.5 mb-2 ${tone}`}><Icon className="w-4 h-4" /> {title}</p>
    {items.length === 0 && <p className="text-xs text-grey">Veri yok</p>}
    {items.map((x) => (
      <div key={`${x.weekday}-${x.start_time}`} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
        <span>{WD_LONG[x.weekday]} {x.start_time}</span>
        <span className="text-xs text-grey flex items-center gap-2">%{x.occupancy} · {x.booked}/{x.capacity}{x.waitlist ? ` · ${x.waitlist} bekleyen` : ""}
          {onExtra && <button onClick={() => onExtra(x)} className="inline-flex items-center gap-0.5 text-emerald-700 hover:underline" data-testid={`extra-class-btn-${x.weekday}-${x.start_time}`}><Plus className="w-3 h-3" />Ek ders</button>}
        </span>
      </div>
    ))}
  </CardContent></Card>
);

export default function GroupOccupancyReport({ businessId }) {
  const [range, setRange] = useState(() => PRESETS[0].range().map(ymd));
  const [data, setData] = useState(null);
  const [extraSlot, setExtraSlot] = useState(null);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/businesses/${businessId}/reports/group-occupancy?start_date=${range[0]}&end_date=${range[1]}`, { credentials: "include" });
    if (res.ok) setData(await res.json());
  }, [businessId, range]);
  useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const cell = (wd, t) => data?.slots.find((x) => x.weekday === wd && x.start_time === t);

  return (
    <div className="space-y-4" data-testid="group-occupancy-report">
      <div className="flex flex-wrap items-end gap-2">
        {PRESETS.map((p) => (
          <Button key={p.k} variant="outline" size="sm" className="rounded-xl" onClick={() => setRange(p.range().map(ymd))} data-testid={`occupancy-preset-${p.k}`}>{p.l}</Button>
        ))}
        <DateInputTR value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} className="rounded-xl w-40" data-testid="occupancy-start" />
        <DateInputTR value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} className="rounded-xl w-40" data-testid="occupancy-end" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Online Ders" value={s.classes || 0} testId="occupancy-stat-classes" />
        <Stat label="Ortalama Doluluk" value={`%${s.occupancy || 0}`} sub={`${s.booked || 0}/${s.capacity || 0} yer`} testId="occupancy-stat-rate" />
        <Stat label="Gelmeme Oranı" value={`%${s.no_show_rate || 0}`} sub={`${s.no_show || 0} kişi gelmedi`} testId="occupancy-stat-noshow" />
        <Stat label="Linkten Rezervasyon" value={s.via_link || 0} testId="occupancy-stat-vialink" />
        <Stat label="Bekleme Listesi Talebi" value={s.waitlist || 0} sub="Dolu derse kayıt olmak isteyen" testId="occupancy-stat-waitlist" />
      </div>

      <Card className="border-0 shadow-sm"><CardContent className="p-4 overflow-x-auto">
        <p className="font-semibold text-navy text-sm mb-3 flex items-center gap-1.5"><Users className="w-4 h-4" /> Gün × Saat Doluluk Haritası</p>
        {data?.times?.length ? (
          <table className="text-xs border-separate border-spacing-1" data-testid="occupancy-heatmap">
            <thead><tr><th className="w-14" />{WD.map((d) => <th key={d} className="px-2 text-grey font-medium min-w-[64px]">{d}</th>)}</tr></thead>
            <tbody>
              {data.times.map((t) => (
                <tr key={t}>
                  <td className="font-mono text-grey pr-1">{t}</td>
                  {WD.map((_, wd) => {
                    const x = cell(wd, t);
                    return (
                      <td key={wd} onClick={() => x && setExtraSlot(x)} className={`relative rounded-lg text-center px-2 py-2 ${x ? `${heat(x.occupancy)} cursor-pointer hover:ring-2 hover:ring-emerald-400` : "bg-white border border-dashed border-slate-200"}`}
                        title={x ? `${WD_LONG[wd]} ${t}: %${x.occupancy} (${x.booked}/${x.capacity}) · ${x.classes} ders · gelmeme %${x.no_show_rate}` : ""}
                        data-testid={x ? `heat-${wd}-${t}` : undefined}>
                        {x ? <><b>%{Math.round(x.occupancy)}</b><br /><span className="opacity-80">{x.booked}/{x.capacity}</span>
                          {x.waitlist > 0 && <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white rounded-full text-[10px] px-1.5 py-0.5 flex items-center gap-0.5 shadow"><Bell className="w-2.5 h-2.5" />{x.waitlist}</span>}</> : ""}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {data?.times?.length ? <p className="text-[11px] text-grey mt-2">Bir hücreye tıklayarak o gün/saate başka bir hocayla ek ders açabilirsiniz. <Bell className="inline w-3 h-3" /> = bekleme listesi.</p> : <p className="text-sm text-grey py-6 text-center" data-testid="occupancy-empty">Bu aralıkta açık grup dersi yok.</p>}
      </CardContent></Card>

      <div className="grid md:grid-cols-2 gap-3">
        <SlotList title="En çok tercih edilen saatler" icon={Flame} tone="text-emerald-700" items={data?.top_slots || []} testId="occupancy-top-slots" onExtra={setExtraSlot} />
        <SlotList title="En boş saatler" icon={Snowflake} tone="text-slate-600" items={data?.bottom_slots || []} testId="occupancy-bottom-slots" />
      </div>
      {extraSlot && <ExtraClassDialog slot={extraSlot} businessId={businessId} onClose={(created) => { setExtraSlot(null); if (created) load(); }} />}
    </div>
  );
}
